import React from "react";
import { Box, Flex, Input, Text } from "@commonsku/design-system";
import { WorkflowConditionInput } from "../../../../types";
import { toTitleCase } from "../../../../utils";
import {
  WorkflowConditionCatalogEntry,
  WorkflowStatusDoesNotChangeEntry,
} from "../../catalog/conditions";
import { TriggerEditorProps } from "./types";

const DAY_DURATION_PATTERN = /^P(\d+)D$/;

const getStalledDays = (
  value: WorkflowConditionInput["value"],
): number | "" => {
  if (typeof value !== "string") {
    return "";
  }

  const match = DAY_DURATION_PATTERN.exec(value);
  return match ? Number(match[1]) : "";
};

const isStalledTriggerOption = (
  option: WorkflowConditionCatalogEntry,
): option is WorkflowStatusDoesNotChangeEntry =>
  option.type === "status_id" && option.operator === "does not change";

/**
 * Trigger editor for a status that has not changed for a configured day count.
 */
export const StalledStatusTriggerEditor = ({
  modelType,
  trigger,
  option,
  onChange,
}: TriggerEditorProps) => {
  const stalledDays = getStalledDays(trigger.value);
  const stalledDaysText = stalledDays === "" ? "" : String(stalledDays);
  const [draftDays, setDraftDays] = React.useState(stalledDaysText);

  React.useEffect(() => {
    setDraftDays(stalledDaysText);
  }, [stalledDaysText]);

  if (!isStalledTriggerOption(option)) {
    return null;
  }

  const modelLabel = toTitleCase(modelType);
  const article = /^[aeiou]/i.test(modelLabel) ? "an" : "a";

  const handleDaysChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const nextValue = event.target.value;
    setDraftDays(nextValue);

    if (nextValue === "") {
      return;
    }

    const days = Number(nextValue);

    if (
      !Number.isInteger(days) ||
      days < option.min_days ||
      days > option.max_days
    ) {
      return;
    }

    onChange({
      ...trigger,
      value: `P${days}D`,
    });
  };

  const handleDaysBlur = () => {
    const days = Number(draftDays);

    if (draftDays === "" || !Number.isInteger(days)) {
      setDraftDays(stalledDaysText);
      return;
    }

    const boundedDays = Math.min(
      option.max_days,
      Math.max(option.min_days, days),
    );

    if (boundedDays !== days) {
      setDraftDays(String(boundedDays));
      onChange({
        ...trigger,
        value: `P${boundedDays}D`,
      });
    }
  };

  return (
    <Flex align="center" flexWrap="wrap" gap="2">
      <Text fontSize="md" fontWeight="medium" lineHeight="unset" margin="0!">
        When {article} {modelLabel} has been stalled for
      </Text>
      <Box flex="none" width="16">
        <Input
          aria-label="Number of days stalled"
          height="10"
          margin="0!"
          max={option.max_days}
          min={option.min_days}
          onBlur={handleDaysBlur}
          onChange={handleDaysChange}
          textAlign="right"
          type="number"
          value={draftDays}
          width="full"
        />
      </Box>
      <Text fontSize="md" fontWeight="medium" lineHeight="unset" margin="0!">
        days
      </Text>
    </Flex>
  );
};

export default StalledStatusTriggerEditor;
